import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, IconButton,
  Tooltip, Typography,
} from '@mui/material';
import {
  Close, Download, History, OpenInNew, Print, Refresh,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { fetchReportPdf, saveBlobToDisk } from '../../services/reportService';
import type { ReportCacheStatus } from '../../types/reportArtifact';
import { ReportHistoryPanel } from './ReportHistoryPanel';

interface ReportPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderDetailKey: string;
}

/**
 * Report preview dialog: renders the test's report PDF in the browser's
 * built-in viewer (blob URL in an iframe — same pattern as QuotePdfPreview),
 * with print, download, regenerate (bypass the archive), and the report
 * history sidebar.
 *
 * Editing workflow (deliberate, no embedded editor): Download → adjust in
 * Adobe Acrobat / Inkscape / LibreOffice Draw → "Upload issued copy" in the
 * history sidebar files the result into the report archive.
 */
export const ReportPreviewDialog = ({ open, onClose, workOrderDetailKey }: ReportPreviewDialogProps) => {
  const theme = useTheme();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState('report.pdf');
  const [cacheStatus, setCacheStatus] = useState<ReportCacheStatus | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [regenerating, setRegenerating] = useState(false);

  const loadReport = useCallback(async (fresh: boolean) => {
    const report = await fetchReportPdf(workOrderDetailKey, { fresh });
    blobRef.current = report.blob;
    setFilename(report.filename);
    setCacheStatus(report.cache);
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(report.blob);
    });
  }, [workOrderDetailKey]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCacheStatus(null);
    loadReport(false)
      .then(() => { if (!cancelled) setLoading(false); })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load the report preview.');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      blobRef.current = null;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [open, loadReport]);

  const handlePrint = () => {
    // Chrome/Edge implement print() on the built-in PDF viewer frame; if the
    // browser refuses cross-frame print, fall back to a tab the user can
    // print from directly.
    try {
      const win = iframeRef.current?.contentWindow;
      if (win) {
        win.focus();
        win.print();
        return;
      }
    } catch { /* fall through */ }
    if (blobUrl) window.open(blobUrl, '_blank');
  };

  const handleDownload = () => {
    if (blobRef.current) saveBlobToDisk(blobRef.current, filename);
  };

  const handleOpenInTab = () => {
    if (blobUrl) window.open(blobUrl, '_blank');
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setLoading(true);
    try {
      await loadReport(true);
      setHistoryRefresh((n) => n + 1);
    } catch {
      setError('Failed to regenerate the report.');
    } finally {
      setRegenerating(false);
      setLoading(false);
    }
  };

  const cacheLabel = cacheStatus === 'hit' ? 'Archived copy' : cacheStatus ? 'Freshly rendered' : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* ── Header bar ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }} noWrap>
          Report Preview — {filename}
        </Typography>
        {cacheLabel && (
          <Chip
            label={cacheLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Re-render from current test data (bypasses the archive)">
          <span>
            <Button
              size="small"
              startIcon={regenerating ? <CircularProgress size={14} /> : <Refresh />}
              onClick={handleRegenerate}
              disabled={loading || regenerating}
              sx={{ textTransform: 'none' }}
            >
              Regenerate
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Print">
          <span>
            <Button
              size="small"
              startIcon={<Print />}
              onClick={handlePrint}
              disabled={loading || !blobUrl}
              sx={{ textTransform: 'none' }}
            >
              Print
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Download the PDF — edit it in Acrobat, then file it back via “Upload issued copy” in History">
          <span>
            <Button
              size="small"
              startIcon={<Download />}
              onClick={handleDownload}
              disabled={loading || !blobUrl}
              sx={{ textTransform: 'none' }}
            >
              Download
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Open in a new browser tab">
          <span>
            <IconButton
              size="small"
              onClick={handleOpenInTab}
              disabled={loading || !blobUrl}
              aria-label="Open in new tab"
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={historyOpen ? 'Hide report history' : 'Show report history'}>
          <IconButton
            size="small"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-label="Toggle report history"
            color={historyOpen ? 'primary' : 'default'}
          >
            <History />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close preview">
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Body: viewer + optional history sidebar ─────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
          {blobUrl && !error && (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              title="Report PDF preview"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
            />
          )}
          {loading && !error && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                zIndex: 1,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Preparing report preview…
              </Typography>
            </Box>
          )}
          {error && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
        </Box>
        {historyOpen && (
          <ReportHistoryPanel
            workOrderDetailKey={workOrderDetailKey}
            refreshToken={historyRefresh}
          />
        )}
      </Box>
    </Dialog>
  );
};
